import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, CircularProgress, Alert,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
    Paper, Avatar, useTheme, useMediaQuery, Grid, Tooltip, IconButton
} from '@mui/material';
import {
    SportsSoccer as GoalIcon,
    Assistant as AssistIcon,
    AccessTime as TimeIcon,
    PlayArrow as GamesIcon,
    Warning as YellowCardIcon,
    Error as RedCardIcon,
    EmojiEvents as TrophyIcon,
    TrendingUp as TrendingUpIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import useDataFetch from '../../utils/useDataFetch';

// ─── Helpers ────────────────────────────────────────────────────────────────
const n = (v) => parseInt(v) || 0;

function topN(arr, key, limit = 3) {
    const sorted = [...arr].sort((a, b) => n(b[key]) - n(a[key]));
    const result = [];
    let rank = 1;
    for (let i = 0; i < sorted.length && result.length < limit; i++) {
        const player = sorted[i];
        if (i > 0 && n(sorted[i][key]) < n(sorted[i - 1][key])) rank = i + 1;
        if (rank > limit) break;
        result.push({ ...player, rank });
    }
    if (result.length > 0) {
        const lastVal = n(result[result.length - 1][key]);
        let j = result.length;
        while (j < sorted.length && n(sorted[j][key]) === lastVal) {
            result.push({ ...sorted[j], rank: result[result.length - 1].rank });
            j++;
        }
    }
    return result;
}

const RANK_LABELS = ['🥇', '🥈', '🥉'];

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatBentoBox = ({ title, value, subtitle, icon, isHighlight }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    return (
        <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: 2,
            bgcolor: isHighlight ? 'primary.main' : 'background.paper',
            color: isHighlight ? '#000' : 'text.primary',
            border: isHighlight ? 'none' : `1px solid ${theme.palette.divider}`,
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: isHighlight ? '0 20px 40px -10px rgba(190, 242, 100, 0.4)' : undefined,
            }
        }}>
            <Box sx={{ 
                position: 'absolute', 
                top: -15, 
                right: -15, 
                opacity: isHighlight ? 0.2 : (isDark ? 0.05 : 0.1),
                transform: 'scale(2.5)',
                color: isHighlight ? '#000' : 'text.primary'
            }}>
                {icon}
            </Box>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="overline" sx={{ fontWeight: 800, opacity: 0.7, letterSpacing: 1 }}>
                    {title}
                </Typography>
                <Typography variant="h2" sx={{ fontWeight: 900, my: 1, lineHeight: 1 }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.8 }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Card>
    );
};

const RankingBentoBox = ({ title, icon, players, valueKey, valueLabel }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    
    return (
        <Card sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{ 
                    p: 1, 
                    borderRadius: "12px", 
                    bgcolor: 'primary.main', 
                    color: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                {players.length > 0 ? players.map((player, idx) => {
                    const rankIdx = Math.min(player.rank - 1, 2);
                    return (
                        <Box
                            key={player.user_id}
                            onClick={() => navigate(`/users/${player.user_id}`)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 1.5,
                                borderRadius: "12px",
                                bgcolor: player.rank === 1 ? (theme.palette.mode === 'dark' ? 'rgba(190, 242, 100, 0.1)' : 'rgba(190, 242, 100, 0.3)') : 'transparent',
                                border: `1px solid ${player.rank === 1 ? 'primary.main' : 'transparent'}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                    transform: 'translateX(4px)'
                                }
                            }}
                        >
                            <Typography sx={{ fontSize: '1.2rem', minWidth: 28, textAlign: 'center' }}>
                                {RANK_LABELS[rankIdx]}
                            </Typography>
                            
                            <Avatar sx={{ 
                                width: 40, 
                                height: 40, 
                                bgcolor: player.rank === 1 ? 'primary.main' : 'text.secondary',
                                color: player.rank === 1 ? '#000' : '#fff',
                                fontWeight: 800
                            }}>
                                {player.first_name[0]}{player.last_name[0]}
                            </Avatar>
                            
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                                    {player.first_name} {player.last_name}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: player.rank === 1 ? 'primary.main' : 'text.primary', lineHeight: 1 }}>
                                    {n(player[valueKey])}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {valueLabel}
                                </Typography>
                            </Box>
                        </Box>
                    );
                }) : (
                    <Typography color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', mt: 4 }}>
                        Noch keine Daten vorhanden
                    </Typography>
                )}
            </Box>
        </Card>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const PlayerStats = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { data, error, isLoading } = useDataFetch({ url: `/stats/team/${teamId}` });

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
                <Alert severity="error" sx={{ borderRadius: "12px" }}>
                    Fehler beim Laden der Statistiken: {error}
                </Alert>
            </Box>
        );
    }

    const stats = data?.stats || [];
    
    // Aggregations
    const totalGoals = stats.reduce((sum, p) => sum + n(p.tore_gesamt), 0);
    const totalAssists = stats.reduce((sum, p) => sum + n(p.vorlagen_gesamt), 0);
    const totalCards = stats.reduce((sum, p) => sum + n(p.gelbe_gesamt) + n(p.rote_gesamt), 0);
    
    // Rankings
    const topScorers = topN(stats, 'tore_gesamt', 3);
    const topAssists = topN(stats, 'vorlagen_gesamt', 3);
    const topMinutes = topN(stats, 'minuten_gesamt', 3);
    const badBoys = topN(stats, 'gelbe_gesamt', 3); // Alternativ kombiniert mit Rot

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
            
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <IconButton onClick={() => navigate(`/teams/${teamId}/overview`)} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.02em' }}>
                        Analytics <Typography component="span" variant="h3" sx={{ color: 'primary.main', fontWeight: 900 }}>Dashboard</Typography>
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {stats.length} aktive Spieler in der Statistik erfasst
                    </Typography>
                </Box>
            </Box>

            {stats.length > 0 ? (
                <Grid container spacing={3}>
                    
                    {/* Hero Stats Row (Bento) */}
                    <Grid >
                        <StatBentoBox 
                            title="GESAMT TORE" 
                            value={totalGoals} 
                            subtitle="In allen gewerteten Spielen"
                            icon={<GoalIcon />} 
                            isHighlight={true} 
                        />
                    </Grid>
                    <Grid item xs={6} >
                        <StatBentoBox 
                            title="VORLAGEN" 
                            value={totalAssists} 
                            icon={<AssistIcon />} 
                            isHighlight={false} 
                        />
                    </Grid>
                    <Grid item xs={6} >
                        <StatBentoBox 
                            title="KARTEN" 
                            value={totalCards} 
                            subtitle="Gelb & Rot kombiniert"
                            icon={<YellowCardIcon />} 
                            isHighlight={false} 
                        />
                    </Grid>

                    {/* Rankings Row */}
                    <Grid >
                        <RankingBentoBox 
                            title="Top Torjäger" 
                            icon={<GoalIcon />} 
                            players={topScorers} 
                            valueKey="tore_gesamt" 
                            valueLabel="Tore" 
                        />
                    </Grid>
                    <Grid >
                        <RankingBentoBox 
                            title="Top Vorlagen" 
                            icon={<AssistIcon />} 
                            players={topAssists} 
                            valueKey="vorlagen_gesamt" 
                            valueLabel="Assists" 
                        />
                    </Grid>
                    <Grid >
                        <RankingBentoBox 
                            title="Dauerbrenner" 
                            icon={<TimeIcon />} 
                            players={topMinutes} 
                            valueKey="minuten_gesamt" 
                            valueLabel="Minuten" 
                        />
                    </Grid>

                    {/* Full Roster Table */}
                    <Grid item xs={12}>
                        <Card sx={{ mt: 2, p: 1 }}>
                            <CardContent>
                                <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TrendingUpIcon sx={{ color: 'primary.main' }} />
                                    Kompletter Kader
                                </Typography>
                                
                                <TableContainer sx={{ overflowX: 'auto' }}>
                                    <Table sx={{ minWidth: 600 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Spieler</TableCell>
                                                <TableCell align="center">Einsätze</TableCell>
                                                <TableCell align="center">Minuten</TableCell>
                                                <TableCell align="center">Tore</TableCell>
                                                <TableCell align="center">Vorlagen</TableCell>
                                                <TableCell align="center">Gelb</TableCell>
                                                <TableCell align="center">Rot</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {stats.sort((a, b) => n(b.tore_gesamt) - n(a.tore_gesamt)).map(player => (
                                                <TableRow 
                                                    key={player.user_id} 
                                                    hover
                                                    onClick={() => navigate(`/users/${player.user_id}`)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: '#000', fontSize: '0.8rem', fontWeight: 700 }}>
                                                                {player.first_name[0]}{player.last_name[0]}
                                                            </Avatar>
                                                            <Typography sx={{ fontWeight: 600 }}>
                                                                {player.first_name} {player.last_name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">{n(player.spiele_gesamt)}</TableCell>
                                                    <TableCell align="center">{n(player.minuten_gesamt)}'</TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'inline-block', minWidth: 30, px: 1, py: 0.5, borderRadius: "12px", bgcolor: theme.palette.mode === 'dark' ? 'rgba(190, 242, 100, 0.1)' : 'rgba(190, 242, 100, 0.3)', color: theme.palette.mode === 'dark' ? 'primary.main' : '#2e7d32', fontWeight: 800 }}>
                                                            {n(player.tore_gesamt)}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'inline-block', minWidth: 30, px: 1, py: 0.5, borderRadius: "12px", bgcolor: theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.1)' : 'rgba(56, 189, 248, 0.2)', color: theme.palette.mode === 'dark' ? '#38BDF8' : '#0284C7', fontWeight: 800 }}>
                                                            {n(player.vorlagen_gesamt)}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {n(player.gelbe_gesamt) > 0 ? (
                                                            <Box sx={{ display: 'inline-block', minWidth: 30, px: 1, py: 0.5, borderRadius: "12px", bgcolor: 'rgba(250, 204, 21, 0.15)', color: '#FACC15', fontWeight: 800 }}>
                                                                {n(player.gelbe_gesamt)}
                                                            </Box>
                                                        ) : '-'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {n(player.rote_gesamt) > 0 ? (
                                                            <Box sx={{ display: 'inline-block', minWidth: 30, px: 1, py: 0.5, borderRadius: "12px", bgcolor: 'rgba(239, 68, 68, 0.15)', color: '#EF4444', fontWeight: 800 }}>
                                                                {n(player.rote_gesamt)}
                                                            </Box>
                                                        ) : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            ) : (
                <Alert severity="info" sx={{ borderRadius: "12px", mt: 4, py: 3, display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Noch keine Statistiken verfügbar</Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                        Sobald Spiele absolviert und die Aufstellungen inklusive Events (Tore, Karten) erfasst wurden, füllt sich dieses Dashboard automatisch mit Leben.
                    </Typography>
                </Alert>
            )}
        </Box>
    );
};

export default PlayerStats;